import React, { useState, useEffect, useRef, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  makeStyles,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Box,
  Typography,
} from "@material-ui/core";
import PublicIcon from "@material-ui/icons/Public";
import PersonIcon from "@material-ui/icons/Person";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { poderDeAdmin } from "../../utils/perfil";

// O atendente digita o caractere e pronto. Aspas em volta viram lixo: quem
// salvava '"/"' so fazia isso porque o formulario antigo exigia 2 caracteres.
const normalizarAtalho = (valor) => {
  let texto = String(valor ?? "").trim();
  let anterior = "";

  while (texto !== anterior) {
    anterior = texto;
    if (
      texto.length >= 2 &&
      ((texto.startsWith('"') && texto.endsWith('"')) ||
        (texto.startsWith("'") && texto.endsWith("'")))
    ) {
      texto = texto.slice(1, -1).trim();
    }
  }

  return texto;
};

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
  },
  title: {
    paddingBottom: 4,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
  },
  field: {
    width: "100%",
  },
  atalhoInput: {
    "& input": {
      fontFamily: "monospace",
      fontSize: "1.05rem",
      fontWeight: 600,
    },
  },
  escopoLinha: {
    display: "flex",
    gap: 12,
    marginTop: 8,
    marginBottom: 4,
    flexWrap: "wrap",
  },
  escopoCard: {
    flex: "1 1 180px",
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "12px 14px",
    borderRadius: 12,
    cursor: "pointer",
    transition: "all .15s ease",
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.02)" : "#fafafa",
  },
  escopoCardAtivo: {
    borderColor: "#1E9855",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(30,152,85,0.14)"
        : "rgba(30,152,85,0.08)",
  },
  escopoIcone: {
    fontSize: 20,
    marginTop: 2,
    color: theme.palette.text.secondary,
  },
  escopoIconeAtivo: {
    color: "#1E9855",
  },
  previa: {
    marginTop: 14,
    padding: "10px 14px",
    borderRadius: 12,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#f3f4f6",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: "#1E9855",
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const QuickAnswerSchema = Yup.object().shape({
  shortcut: Yup.string()
    .trim()
    .min(1, "Digite ao menos um caractere")
    .max(30, "Atalho muito longo")
    .required("Obrigatório"),
  message: Yup.string()
    .trim()
    .min(1, "Escreva a mensagem")
    .max(30000, "Mensagem muito longa")
    .required("Obrigatório"),
});

const QuickAnswersModal = ({
  open,
  onClose,
  quickAnswerId,
  initialValues,
  onSave,
}) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);

  const ehAdmin = poderDeAdmin(user?.profile);

  const initialState = {
    shortcut: "",
    message: "",
    scope: ehAdmin ? "global" : "user",
  };

  const [quickAnswer, setQuickAnswer] = useState(initialState);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchQuickAnswer = async () => {
      if (initialValues) {
        setQuickAnswer((prevState) => ({ ...prevState, ...initialValues }));
      }

      if (!quickAnswerId) return;

      try {
        const { data } = await api.get(`/quickAnswers/${quickAnswerId}`);
        if (isMounted.current) {
          setQuickAnswer({
            shortcut: data.shortcut || "",
            message: data.message || "",
            scope: data.userId ? "user" : "global",
          });
        }
      } catch (err) {
        toastError(err);
      }
    };

    fetchQuickAnswer();
  }, [quickAnswerId, open, initialValues]);

  const handleClose = () => {
    onClose();
    setQuickAnswer(initialState);
  };

  const handleSaveQuickAnswer = async (values) => {
    const payload = {
      ...values,
      shortcut: normalizarAtalho(values.shortcut),
      scope: ehAdmin ? values.scope : "user",
    };

    try {
      if (quickAnswerId) {
        await api.put(`/quickAnswers/${quickAnswerId}`, payload);
        handleClose();
      } else {
        const { data } = await api.post("/quickAnswers", payload);
        if (onSave) onSave(data);
        handleClose();
      }
      toast.success("Resposta rápida salva.");
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{ className: classes.dialogPaper }}
    >
      <DialogTitle className={classes.title}>
        {quickAnswerId ? "Editar resposta rápida" : "Nova resposta rápida"}
        <Typography className={classes.subtitle}>
          Digite o atalho e a mensagem. Na conversa, é só digitar o atalho para
          a resposta aparecer.
        </Typography>
      </DialogTitle>

      <Formik
        initialValues={quickAnswer}
        enableReinitialize={true}
        validationSchema={QuickAnswerSchema}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            handleSaveQuickAnswer(values);
            actions.setSubmitting(false);
          }, 200);
        }}
      >
        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              <Field
                as={TextField}
                label="Atalho"
                name="shortcut"
                autoFocus
                error={touched.shortcut && Boolean(errors.shortcut)}
                helperText={
                  (touched.shortcut && errors.shortcut) ||
                  'Só o caractere ou a palavra — sem aspas. Ex.: / ou #bemvindo'
                }
                variant="outlined"
                margin="dense"
                className={`${classes.field} ${classes.atalhoInput}`}
                onBlur={() =>
                  setFieldValue("shortcut", normalizarAtalho(values.shortcut))
                }
                fullWidth
              />

              <Field
                as={TextField}
                label="Mensagem"
                name="message"
                error={touched.message && Boolean(errors.message)}
                helperText={touched.message && errors.message}
                variant="outlined"
                margin="dense"
                className={classes.field}
                multiline
                rows={6}
                fullWidth
              />

              {ehAdmin && (
                <Box mt={2}>
                  <Typography
                    variant="subtitle2"
                    style={{ fontWeight: 600, marginBottom: 4 }}
                  >
                    Quem pode usar
                  </Typography>

                  <Box className={classes.escopoLinha}>
                    <Box
                      className={`${classes.escopoCard} ${
                        values.scope === "global" ? classes.escopoCardAtivo : ""
                      }`}
                      onClick={() => setFieldValue("scope", "global")}
                    >
                      <PublicIcon
                        className={`${classes.escopoIcone} ${
                          values.scope === "global"
                            ? classes.escopoIconeAtivo
                            : ""
                        }`}
                      />
                      <Box>
                        <Typography
                          variant="body2"
                          style={{ fontWeight: 600 }}
                        >
                          Global
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Todos os atendentes enxergam
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      className={`${classes.escopoCard} ${
                        values.scope === "user" ? classes.escopoCardAtivo : ""
                      }`}
                      onClick={() => setFieldValue("scope", "user")}
                    >
                      <PersonIcon
                        className={`${classes.escopoIcone} ${
                          values.scope === "user" ? classes.escopoIconeAtivo : ""
                        }`}
                      />
                      <Box>
                        <Typography
                          variant="body2"
                          style={{ fontWeight: 600 }}
                        >
                          Só minha
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Aparece apenas para você
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}

              {normalizarAtalho(values.shortcut) && (
                <Box className={classes.previa}>
                  <Typography variant="caption" color="textSecondary">
                    Na conversa vai aparecer assim:
                  </Typography>
                  <Typography variant="body2" style={{ marginTop: 4 }}>
                    <strong style={{ fontFamily: "monospace" }}>
                      {normalizarAtalho(values.shortcut)}
                    </strong>
                    {values.message ? ` — ${values.message.slice(0, 70)}` : ""}
                    {values.message && values.message.length > 70 ? "…" : ""}
                  </Typography>
                </Box>
              )}
            </DialogContent>

            <DialogActions>
              <Button
                onClick={handleClose}
                color="secondary"
                disabled={isSubmitting}
                variant="outlined"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={isSubmitting}
                variant="contained"
                className={classes.btnWrapper}
              >
                Salvar
                {isSubmitting && (
                  <CircularProgress
                    size={24}
                    className={classes.buttonProgress}
                  />
                )}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default QuickAnswersModal;
